// Rules and rule groups as the Rules & classes screens need them, and the
// regime limits read back from the live rules (so the bands, "in range" and
// the alarms always agree with what the rule engine runs).
import { tenantApi } from "../lib/account";
import { REGIMES, loadClasses, rangeOf, classesVersion } from "./classes";

export interface RuleAction { type: string; alarm?: string; severity?: string; channel_id?: string; [k: string]: unknown }
export interface Rule { id: string; name: string; enabled: boolean; trigger: { on: string; hours?: number }; condition: string; timing?: { for?: string; clear?: string; cooldown?: string }; actions: RuleAction[]; owner_account?: string }
export interface Group { id: string; name: string; kind: string; rule?: string; priority?: number }
export interface GroupRules { group: Group; rules: Rule[]; members: string[] }

export const TEMP = '("ext_temperature" in state ? num(state, "ext_temperature", 0.0) : num(state, "temperature_1", 0.0))';
export const HAS = '("ext_temperature" in state || "temperature_1" in state)';
export const regimeOf = (g: Group) => /(?:cold_class|temperature_regime)", ""\) == "([\w-]+)"/.exec(g.rule || "")?.[1] || null;
/** the number a temperature condition compares with (the last "> x" / "< x") */
export const limitOf = (cond: string) => { const m = /([<>]=?)\s*(-?\d+(?:\.\d+)?)\s*$/.exec(cond.trim()); return m ? Number(m[2]) : null; };

export async function loadRulesModel(): Promise<{ rules: Rule[]; groups: GroupRules[] }> {
  const [r, g] = await Promise.all([tenantApi<{ items: Rule[] }>("GET", "/rules"), tenantApi<{ items: Group[] }>("GET", "/groups"), loadClasses().catch(() => null)]);
  const byId = new Map(r.items.map((x) => [x.id, x]));
  const groups = await Promise.all(g.items.map(async (grp) => {
    const [gr, mem] = await Promise.all([
      tenantApi<{ own: { rule: { id: string } }[] }>("GET", `/groups/${grp.id}/rules`).catch(() => ({ own: [] })),
      tenantApi<{ items: { device_id: string }[] }>("GET", `/groups/${grp.id}/members?page_size=500`).catch(() => ({ items: [] })),
    ]);
    return { group: grp, rules: gr.own.map((x) => byId.get(x.rule.id)).filter((x): x is Rule => !!x), members: mem.items.map((m) => m.device_id) };
  }));
  // the regime limits as the rules hold them
  for (const gr of groups) {
    const reg = regimeOf(gr.group);
    if (!reg || !REGIMES[reg]) continue;
    for (const rule of gr.rules) {
      const a = rule.actions.find((x) => x.type === "alarm")?.alarm;
      const v = limitOf(rule.condition);
      if (v == null) continue;
      if (a === "TEMP_WARNING") REGIMES[reg].warn = v;
      if (a === "TEMP_HIGH") REGIMES[reg].alarm = v;
      if (a === "TEMP_CRITICAL") REGIMES[reg].crit = v;
      if (a === "TEMP_LOW") REGIMES[reg].low = v;
    }
  }
  for (const c of Object.values(REGIMES)) c.range = rangeOf(c);
  classesVersion.value++;
  return { rules: r.items, groups };
}

/** replay a rule over the last days with the engine's own code; fired alarms per day */
export async function replay(rule: Rule, deviceIds: string[], days = 30) {
  const to = Date.now(), from = to - days * 864e5;
  const r = await tenantApi<{ events: { at: string; kind: string }[] }>("POST", "/rules/simulate", { rule, device_ids: deviceIds, from: new Date(from).toISOString(), step: "15m" });
  const perDay = Array.from({ length: days }, () => 0);
  for (const e of r.events) if (e.kind === "fired") { const d = Math.floor((Date.parse(e.at) - from) / 864e5); if (d >= 0 && d < days) perDay[d]++; }
  return { perDay, total: perDay.reduce((a, b) => a + b, 0) };
}
